import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { Calendar, MapPin, Users, Wallet, ArrowRight, Shield } from "lucide-react";
import { format } from "date-fns";

const JoinKrew = () => {
  const { code } = useParams<{ code: string }>();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [krew, setKrew] = useState<any>(null);
  const [memberCount, setMemberCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);
  const [alreadyMember, setAlreadyMember] = useState(false);

  useEffect(() => {
    if (!code) return;
    const fetchKrew = async () => {
      const { data } = await supabase
        .rpc("get_krew_by_invite_code", { code })
        .single();

      if (data) {
        setKrew(data);
        const { count } = await supabase
          .from("krew_members")
          .select("*", { count: "exact", head: true })
          .eq("krew_id", data.id);
        setMemberCount(count || 0);

        if (user) {
          const { data: existing } = await supabase
            .from("krew_members")
            .select("id")
            .eq("krew_id", data.id)
            .eq("user_id", user.id)
            .maybeSingle();
          if (existing) setAlreadyMember(true);
        }
      }
      setLoading(false);
    };
    fetchKrew();
  }, [code, user]);

  const handleJoin = async () => {
    if (!user) {
      navigate(`/auth?redirect=/join/${code}`);
      return;
    }
    if (!krew) return;
    setJoining(true);

    try {
      const { error } = await supabase.from("krew_members").insert({
        krew_id: krew.id,
        user_id: user.id,
        status: "invited",
      });
      if (error) throw error;
      toast({ title: "You're in! 🎉", description: "Commit your deposit to lock your spot." });
      navigate(`/krew/${krew.id}`);
    } catch (error: any) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } finally {
      setJoining(false);
    }
  };

  if (loading || authLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  if (!krew) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="text-center">
          <h1 className="mb-2 text-2xl font-bold text-foreground">Invite not found</h1>
          <p className="text-muted-foreground">This link may have expired or is invalid.</p>
        </div>
      </div>
    );
  }

  if (alreadyMember) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="text-center">
          <h1 className="mb-2 text-2xl font-bold text-foreground">You're already in!</h1>
          <p className="mb-6 text-muted-foreground">You're a member of this Krew.</p>
          <button
            onClick={() => navigate(`/krew/${krew.id}`)}
            className="inline-flex items-center gap-2 rounded-xl bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground glow-neon"
          >
            Go to Krew <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="mb-6 text-center">
          <h1 className="text-2xl font-bold text-gradient-neon">Krew</h1>
          <p className="text-sm text-muted-foreground">You've been invited!</p>
        </div>

        <div className="rounded-2xl border border-border bg-card p-6">
          <h2 className="mb-4 text-2xl font-bold text-foreground">{krew.title}</h2>
          {krew.description && <p className="mb-4 text-sm text-muted-foreground">{krew.description}</p>}

          <div className="mb-6 space-y-3 text-sm text-muted-foreground">
            <div className="flex items-center gap-3">
              <Calendar className="h-4 w-4 text-primary" />
              {format(new Date(krew.event_date), "EEEE, MMMM d")} · {krew.event_time?.slice(0, 5)}
            </div>
            <div className="flex items-center gap-3">
              <MapPin className="h-4 w-4 text-primary" />
              {krew.area || krew.city}
            </div>
            <div className="flex items-center gap-3">
              <Users className="h-4 w-4 text-primary" />
              {memberCount}/{krew.max_members} spots taken
            </div>
            <div className="flex items-center gap-3">
              <Wallet className="h-4 w-4 text-secondary" />
              ₹{krew.budget_per_person.toLocaleString()} per person
            </div>
            <div className="flex items-center gap-3">
              <Shield className="h-4 w-4 text-primary" />
              ₹{krew.commitment_amount} commitment deposit
            </div>
          </div>

          {krew.vibe && krew.vibe.length > 0 && (
            <div className="mb-6 flex flex-wrap gap-2">
              {krew.vibe.map((v: string) => (
                <span key={v} className="rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
                  {v}
                </span>
              ))}
            </div>
          )}

          {memberCount >= krew.max_members ? (
            <p className="text-center text-sm font-medium text-destructive">This Krew is full</p>
          ) : (
            <button
              onClick={handleJoin}
              disabled={joining}
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-primary px-6 py-3.5 text-sm font-semibold text-primary-foreground transition-all glow-neon hover:scale-[1.02] disabled:opacity-50"
            >
              {joining ? (
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary-foreground border-t-transparent" />
              ) : (
                <>
                  Join This Krew <ArrowRight className="h-4 w-4" />
                </>
              )}
            </button>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default JoinKrew;
